import { useEffect, useRef } from "react";

const PETAL_COLORS = [
  "#f9a8d4", // pink-300
  "#f472b6", // pink-400
  "#ec4899", // pink-500
  "#fbcfe8", // pink-200
  "#fce7f3", // pink-100
  "#e879f9", // fuchsia-400
  "#c084fc", // violet-400
];

interface Petal {
  x: number;
  y: number;
  size: number;
  opacity: number;
  rotation: number;
  rotationSpeed: number;
  fallSpeed: number;
  swayAmount: number;
  swayPhase: number;
  color: string;
}

function createPetal(canvasWidth: number, canvasHeight: number): Petal {
  return {
    x: Math.random() * canvasWidth,
    y: Math.random() * canvasHeight,
    size: 4 + Math.random() * 12,
    opacity: 0.4 + Math.random() * 0.5,
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.02,
    fallSpeed: 0.3 + Math.random() * 1.2,
    swayAmount: 15 + Math.random() * 35,
    swayPhase: Math.random() * Math.PI * 2,
    color: PETAL_COLORS[Math.floor(Math.random() * PETAL_COLORS.length)],
  };
}

/** Falling sakura petals with wind sway for Sakura premium theme */
export default function SakuraPetals() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let petals: Petal[] = [];
    let time = 0;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
      const count = Math.min(80, Math.floor((w * h) / 18000));
      if (petals.length === 0) {
        petals = Array.from({ length: count }, () => createPetal(w, h));
      } else {
        petals = petals.map((p) => ({
          ...p,
          x: p.x > w ? Math.random() * w : p.x,
          y: p.y > h ? -20 : p.y,
        }));
      }
    };

    const drawPetal = (p: Petal, t: number) => {
      ctx.save();
      const swayX = Math.sin(t * 0.002 + p.swayPhase) * p.swayAmount;
      const cx = p.x + swayX;
      const cy = p.y;
      ctx.translate(cx, cy);
      ctx.rotate(p.rotation + t * p.rotationSpeed);
      ctx.globalAlpha = p.opacity * (0.7 + 0.3 * Math.sin(t * 0.001 + p.swayPhase));
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.ellipse(0, 0, p.size * 0.6, p.size, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    const draw = () => {
      time += 1;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      petals.forEach((p) => {
        p.y += p.fallSpeed;
        p.x += Math.sin(time * 0.003 + p.swayPhase) * 0.4;
        if (p.y > h + p.size * 2) {
          p.y = -p.size * 2;
          p.x = Math.random() * w;
        }
        if (p.x < -50) p.x = w + 50;
        if (p.x > w + 50) p.x = -50;
        drawPetal(p, time);
      });

      animationId = requestAnimationFrame(draw);
    };

    resize();
    draw();

    const handleResize = () => resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
}
